import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { contentService } from '../services/contentService';
import {
  buildStickyAudioMapFromApi,
  hasStickyAudioForBlock,
  mergeBlockStickyAudioMap,
  normalizeStickyAudioPhraseKey,
  textHasStickyAudioMatches,
  type MergeBlockStickyAudioOptions,
  StickyAudioMap
} from '../utils/stickyAudio';
import { useWordHelpOptional } from './WordHelpContext';
import { playStickyAudio } from '../utils/stickyAudioPlayback';
import { WORD_HELP_OPEN_EVENT } from '../utils/wordHelp';
import {
  clearStickyAudioUnderlineTravel,
  clearStickyUnderlineMotion,
  listStickyAudioMarks,
  measureStickyAudioUnderlineTravel,
  STICKY_AUDIO_UNDERLINE_ANIM_MS
} from '../utils/stickyAudioUnderlineAnimation';
import { prefersReducedMotion } from '../utils/subscriptionAccessibility';

const EMPTY_EXIT_PHRASES: ReadonlySet<string> = new Set();

interface StickyAudioContextValue {
  map: StickyAudioMap;
  loading: boolean;
  refreshMap: () => Promise<void>;
  isBlockEnabled: (blockId: number) => boolean;
  setBlockEnabled: (blockId: number, enabled: boolean) => void;
  toggleBlock: (blockId: number) => void;
}

const StickyAudioContext = createContext<StickyAudioContextValue | null>(null);

/**
 * In-memory phrase index only (~1–1.5k entries × phrase + URL strings).
 * Audio files are never preloaded; playback loads on tap.
 */
const STICKY_AUDIO_CACHE_TTL_MS = 5 * 60 * 1000;

let cachedMap: StickyAudioMap | null = null;
let cachedAt = 0;
let cachePromise: Promise<StickyAudioMap> | null = null;

async function loadStickyAudioMap(force = false): Promise<StickyAudioMap> {
  const cacheFresh =
    !force && cachedMap && Date.now() - cachedAt < STICKY_AUDIO_CACHE_TTL_MS;
  if (cacheFresh && cachedMap) return cachedMap;

  if (force) {
    cachedMap = null;
    cachePromise = null;
  }

  if (cachedMap && !force) return cachedMap;

  if (!cachePromise || force) {
    cachePromise = contentService
      .getStickyAudioMap()
      .then((entries) => {
        cachedMap = buildStickyAudioMapFromApi(entries);
        cachedAt = Date.now();
        return cachedMap;
      })
      .catch(() => {
        cachePromise = null;
        return cachedMap ?? {};
      });
  }

  return cachePromise;
}

export function invalidateStickyAudioCache(): void {
  cachedMap = null;
  cachedAt = 0;
  cachePromise = null;
}

export const StickyAudioProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [map, setMap] = useState<StickyAudioMap>(cachedMap ?? {});
  const [loading, setLoading] = useState(!cachedMap);
  const [blockStates, setBlockStates] = useState<Record<number, boolean>>({});

  const refreshMapInternal = useCallback(async (force = false) => {
    setLoading(true);
    const next = await loadStickyAudioMap(force);
    setMap(next);
    setLoading(false);
  }, []);

  useEffect(() => {
    let mounted = true;
    loadStickyAudioMap(false).then((next) => {
      if (mounted) {
        setMap(next);
        setLoading(false);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  const isBlockEnabled = useCallback(
    (blockId: number) => blockStates[blockId] !== false,
    [blockStates]
  );

  const setBlockEnabled = useCallback((blockId: number, enabled: boolean) => {
    setBlockStates((prev) => ({ ...prev, [blockId]: enabled }));
  }, []);

  const toggleBlock = useCallback((blockId: number) => {
    setBlockStates((prev) => ({
      ...prev,
      [blockId]: prev[blockId] === false
    }));
  }, []);

  const value = useMemo(
    () => ({
      map,
      loading,
      refreshMap: () => refreshMapInternal(false),
      isBlockEnabled,
      setBlockEnabled,
      toggleBlock
    }),
    [map, loading, refreshMapInternal, isBlockEnabled, setBlockEnabled, toggleBlock]
  );

  return (
    <StickyAudioContext.Provider value={value}>{children}</StickyAudioContext.Provider>
  );
};

export function useStickyAudio(): StickyAudioContextValue {
  const ctx = useContext(StickyAudioContext);
  if (!ctx) {
    return {
      map: {},
      loading: false,
      refreshMap: async () => {},
      isBlockEnabled: () => true,
      setBlockEnabled: () => {},
      toggleBlock: () => {}
    };
  }
  return ctx;
}

/** Refresh global phrase map when stale (e.g. opening lesson/book). */
export function useRefreshStickyAudioMap(): void {
  const { refreshMap } = useStickyAudio();
  useEffect(() => {
    void refreshMap();
  }, [refreshMap]);
}

function openWordHelp(target: HTMLElement): void {
  const url = target.getAttribute('data-sticky-audio-url') ?? '';
  if (url) playStickyAudio(url);
  const idRaw = target.getAttribute('data-word-help-id');
  const vocabId = idRaw ? Number(idRaw) : NaN;
  if (!Number.isFinite(vocabId)) return;
  const rect = target.getBoundingClientRect();
  window.dispatchEvent(
    new CustomEvent(WORD_HELP_OPEN_EVENT, {
      detail: {
        vocabId,
        audioUrl: url,
        rect: {
          top: rect.top,
          left: rect.left,
          bottom: rect.bottom,
          right: rect.right,
          width: rect.width,
          height: rect.height
        },
        anchor: target
      }
    })
  );
}

function findStickyPhraseLayer(container: HTMLElement): HTMLElement | null {
  if (container.classList.contains('sticky-audio-phrase-layer')) return container;
  return container.querySelector<HTMLElement>('.sticky-audio-phrase-layer');
}

/**
 * Words already on screen stay put. New underlines sit off-screen for one painted
 * frame, then slide in. Setting `shown` in the same turn skips that slide.
 */
function showStickyUnderlines(
  rows: Array<{ mark: HTMLElement; key: string }>,
  settled: Set<string>,
  enterGen: { current: number },
  onEntered: (keys: string[]) => void
): void {
  const incoming: Array<{ mark: HTMLElement; key: string }> = [];
  rows.forEach(({ mark, key }) => {
    mark.style.pointerEvents = '';
    if (settled.has(key)) {
      mark.setAttribute('data-sticky-underline', 'shown');
      mark.setAttribute('data-sticky-instant', '1');
      return;
    }
    mark.setAttribute('data-sticky-underline', 'pending');
    mark.removeAttribute('data-sticky-instant');
    incoming.push({ mark, key });
  });
  if (incoming.length === 0) return;
  incoming.forEach(({ mark }) => {
    void getComputedStyle(mark, '::after').transform;
  });
  const gen = ++enterGen.current;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (enterGen.current !== gen) return;
      const keys: string[] = [];
      incoming.forEach(({ mark, key }) => {
        if (!mark.isConnected || mark.getAttribute('data-sticky-underline') !== 'pending') return;
        mark.setAttribute('data-sticky-underline', 'shown');
        keys.push(key);
      });
      if (keys.length > 0) onEntered(keys);
    });
  });
}

/** Event delegation: click / Enter on `.sticky-audio-mark` plays audio. */
export const StickyAudioInteractionLayer: React.FC<{
  containerRef: React.RefObject<HTMLElement | null>;
  enabled: boolean;
  /** When phrase markup changes (e.g. UI locale), remeasure underline travel. */
  contentKey?: string;
  /** Limits underline enter/exit to this text block's word-help scope. */
  blockId?: number;
}> = ({ containerRef, enabled, contentKey, blockId }) => {
  const settledMarksRef = useRef<Set<string>>(new Set());
  const enterGenRef = useRef(0);
  const rememberEntered = (keys: string[]) => {
    const next = new Set(settledMarksRef.current);
    keys.forEach((key) => next.add(key));
    settledMarksRef.current = next;
  };
  /** While true, a DOM refresh must not paint underlines back onto words that are leaving. */
  const revealPausedRef = useRef(false);
  const wordHelp = useWordHelpOptional();
  const exit = blockId != null ? wordHelp?.exitFor(blockId) : undefined;
  const underlineExitId = exit?.id ?? 0;
  const exitingPhrases = exit?.phrases;
  const exitingRef = useRef(exitingPhrases);
  exitingRef.current = exitingPhrases;

  useLayoutEffect(() => {
    if (!enabled || underlineExitId === 0) return;
    const container = containerRef.current;
    if (!container) return;
    const layer = findStickyPhraseLayer(container);
    if (!layer) return;
    const rows = listStickyAudioMarks(layer);
    const leaving = exitingPhrases ?? EMPTY_EXIT_PHRASES;

    if (leaving.size === 0) {
      revealPausedRef.current = false;
      const settled = new Set(settledMarksRef.current);
      measureStickyAudioUnderlineTravel(layer);
      showStickyUnderlines(rows, settled, enterGenRef, rememberEntered);
      settledMarksRef.current = settled;
      return;
    }

    revealPausedRef.current = true;
    measureStickyAudioUnderlineTravel(layer);
    const settled = new Set(settledMarksRef.current);
    const departing: HTMLElement[] = [];
    rows.forEach(({ mark, key }) => {
      const collapsed = key.slice(0, key.lastIndexOf('\0'));
      const phrase = normalizeStickyAudioPhraseKey(mark.textContent ?? '');
      if (!leaving.has(phrase) && !leaving.has(collapsed)) return;
      mark.removeAttribute('data-sticky-instant');
      departing.push(mark);
      settled.delete(key);
    });
    settledMarksRef.current = settled;
    if (departing.length === 0) return;
    void layer.getBoundingClientRect();
    departing.forEach((mark) => {
      if (!mark.isConnected) return;
      mark.removeAttribute('data-sticky-underline');
      mark.style.pointerEvents = 'none';
    });
  }, [containerRef, enabled, underlineExitId, exitingPhrases]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const layer = findStickyPhraseLayer(container);
    if (!layer) return;

    const reduced = prefersReducedMotion();
    const rows = listStickyAudioMarks(layer);

    if (!enabled) {
      revealPausedRef.current = true;
      enterGenRef.current += 1;
      settledMarksRef.current = new Set();
      if (reduced) {
        rows.forEach(({ mark }) => clearStickyUnderlineMotion(mark));
        clearStickyAudioUnderlineTravel(layer);
        return;
      }
      measureStickyAudioUnderlineTravel(layer);
      rows.forEach(({ mark }) => clearStickyUnderlineMotion(mark));
      const exitTimer = window.setTimeout(() => {
        clearStickyAudioUnderlineTravel(layer);
      }, STICKY_AUDIO_UNDERLINE_ANIM_MS + 40);
      return () => window.clearTimeout(exitTimer);
    }

    revealPausedRef.current = (exitingRef.current?.size ?? 0) > 0;
    if (revealPausedRef.current) return;

    measureStickyAudioUnderlineTravel(layer);
    const settled = reduced ? new Set(rows.map(({ key }) => key)) : new Set(settledMarksRef.current);
    if (reduced) {
      rows.forEach(({ mark }) => {
        mark.style.pointerEvents = '';
        mark.setAttribute('data-sticky-underline', 'shown');
        mark.setAttribute('data-sticky-instant', '1');
      });
      settledMarksRef.current = settled;
      return;
    }
    showStickyUnderlines(rows, settled, enterGenRef, rememberEntered);
    settledMarksRef.current = settled;
  }, [containerRef, enabled, contentKey]);

  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;

    const promote = () => {
      if (revealPausedRef.current) return;
      if ((exitingRef.current?.size ?? 0) > 0) return;
      const layer = findStickyPhraseLayer(container);
      if (!layer) return;
      const settled = new Set(settledMarksRef.current);
      let added = false;
      listStickyAudioMarks(layer).forEach(({ mark, key }) => {
        if (!mark.isConnected) return;
        const state = mark.getAttribute('data-sticky-underline');
        if (state === 'shown') {
          settled.add(key);
          return;
        }
        if (state === 'pending') return;
        mark.style.pointerEvents = '';
        mark.setAttribute('data-sticky-underline', 'shown');
        mark.setAttribute('data-sticky-instant', '1');
        settled.add(key);
        added = true;
      });
      if (added) settledMarksRef.current = settled;
    };

    promote();
    const observer = new MutationObserver(promote);
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [containerRef, enabled, contentKey]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    const handleClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest(
        '.sticky-audio-mark'
      ) as HTMLElement | null;
      if (!target || !container.contains(target)) return;
      e.preventDefault();
      e.stopPropagation();
      openWordHelp(target);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const target = (e.target as HTMLElement).closest(
        '.sticky-audio-mark'
      ) as HTMLElement | null;
      if (!target || !container.contains(target)) return;
      e.preventDefault();
      openWordHelp(target);
    };

    container.addEventListener('click', handleClick);
    container.addEventListener('keydown', handleKeyDown);
    return () => {
      container.removeEventListener('click', handleClick);
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [containerRef, enabled]);

  return null;
};

function readFormattedContentHtml(textFormatting?: string | null): string | undefined {
  if (!textFormatting) return undefined;
  try {
    const parsed = JSON.parse(textFormatting) as { formattedContent?: string };
    return typeof parsed.formattedContent === 'string' ? parsed.formattedContent : undefined;
  } catch {
    return undefined;
  }
}

export function useStickyAudioBlockToggle(
  blockId: number,
  blockStickyJson?: string | null,
  textContent?: string | null,
  textFormatting?: string | null,
  mergeOptions?: MergeBlockStickyAudioOptions
) {
  const wordHelp = useWordHelpOptional();
  const { isBlockEnabled, toggleBlock, map } = useStickyAudio();
  const enabled = isBlockEnabled(blockId);
  const formattedContentHtml = useMemo(
    () => readFormattedContentHtml(textFormatting),
    [textFormatting]
  );
  const mapFor = wordHelp?.mapFor;
  const effectiveMap = useMemo(
    () =>
      mapFor
        ? mapFor(blockId)
        : mergeBlockStickyAudioMap(map, blockStickyJson, mergeOptions),
    [mapFor, blockId, map, blockStickyJson, mergeOptions]
  );
  const hasAny = wordHelp
    ? textHasStickyAudioMatches(textContent, effectiveMap) ||
      textHasStickyAudioMatches(
        formattedContentHtml?.replace(/<[^>]+>/g, ' '),
        effectiveMap
      )
    : hasStickyAudioForBlock(
        map,
        blockStickyJson,
        textContent,
        formattedContentHtml,
        mergeOptions
      );
  return {
    enabled,
    toggleBlock: () => toggleBlock(blockId),
    hasAny,
    effectiveMap
  };
}
